import { Smartphone } from "lucide-react";
import React, { useMemo } from "react";
import { Alert, Divider } from "@mui/material";
import AudioPlayerForRouting from "../reusables/AudioPlayerForRouting";
import TooltipComponent from "../reusables/TooltipComponent";
import RouteSelect from "../reusables/RouteSelect";
import {
  getTwilanswerAppUserOptions,
  parseDeviceUserId,
} from "../../utils/forwardDeviceDestinations";

/**
 * Twilanswer App call action UI — pick a user whose mobile app should ring.
 */
const TwilanswerAppUI = ({
  numberValue,
  recordingValue,
  handleNumberChange,
  handleRecordingChange,
  containerClass,
  headerSize,
  showDivider,
  setOpenConfigureSlotModal,
  recordings = [],
  users = [],
  hasValidationError = false,
  addRecordingOption,
}) => {
  const userOptions = useMemo(
    () => getTwilanswerAppUserOptions(users),
    [users]
  );

  const selectedUserId = parseDeviceUserId(numberValue);
  const selectedUser =
    selectedUserId != null && selectedUserId !== ""
      ? users.find((u) => u.userId === selectedUserId)
      : null;
  const selectedHasNoDevice =
    !!selectedUser && !selectedUser.hasForwardingDevices;
  const anyoneHasDevice = users.some((u) => u.hasForwardingDevices);

  const recordingOptions = useMemo(() => {
    if (!recordings) return [];
    return recordings.map((item) => ({
      value: item.M.rec_id.S,
      label: item.M.title.S,
    }));
  }, [recordings]);

  const recordingUrl = recordingValue
    ? recordings?.find((e) => e.M.rec_id.S === recordingValue)?.M.url.S
    : null;

  const handleUserSelect = (selected) => {
    if (!selected) return;
    const opt = userOptions.find((o) => o.value === selected);
    if (opt?.disabled) return;
    handleNumberChange(selected);
  };

  const renderUserOption = (opt) => (
    <div className={opt.disabled ? "opacity-60" : ""}>
      <div className="text-sm text-gray-900">{opt.label}</div>
      {opt.subtitle && (
        <div className="text-xs text-gray-500">{opt.subtitle}</div>
      )}
    </div>
  );

  return (
    <div>
      <div
        className={`${containerClass}${hasValidationError ? " !bg-red-50/80" : ""}`}
      >
        <div className="flex flex-nowrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center min-w-0 flex-1">
            <Smartphone className="text-green-500 shrink-0 mr-2 h-5 w-5" />
            <div className="min-w-0">
              <h2 className={`${headerSize} text-gray-800`}>Twilanswer App</h2>
              <div className="text-[13px]">
                Ring a team member&apos;s Twilanswer mobile app
              </div>
            </div>
          </div>
          {setOpenConfigureSlotModal && (
            <button
              type="button"
              className="shrink-0 whitespace-nowrap text-sm text-blue-600 font-medium hover:underline text-[13px]"
              onClick={() => setOpenConfigureSlotModal(true)}
            >
              ↻ Change
            </button>
          )}
        </div>
        {showDivider && <Divider />}

        {!anyoneHasDevice && users.length > 0 && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            No one in this organization has the Twilanswer mobile app registered
            yet. Ask a team member to sign in on the app before using this
            action.
          </Alert>
        )}

        {selectedHasNoDevice && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {selectedUser?.name || "This user"} no longer has a Twilanswer app
            registered. Choose another user, or ask them to sign in on the app.
          </Alert>
        )}

        <div className="mb-4">
          <div className="flex items-center gap-1 mb-1">
            <span className="text-sm font-medium text-gray-700">User</span>
            <TooltipComponent message="Select whose Twilanswer mobile app should ring. All of that user's registered devices ring in parallel." />
          </div>
          <RouteSelect
            value={
              selectedUserId != null && selectedUserId !== ""
                ? numberValue
                : ""
            }
            onChange={handleUserSelect}
            options={userOptions}
            placeholder="Select a user"
            renderOptionLabel={renderUserOption}
          />
        </div>

        <div className="mb-4">
          <div className="flex items-center gap-1 mb-1">
            <span className="text-sm font-medium text-gray-700">
              Voicemail Recording
            </span>
            <TooltipComponent message="Required. Unanswered calls play this recording as the greeting and the caller can leave a Twilanswer voicemail." />
          </div>
          <RouteSelect
            value={recordingValue}
            onChange={handleRecordingChange}
            options={recordingOptions}
            placeholder="Select a recording"
            addOption={addRecordingOption}
          />
          {recordingUrl && (
            <div className="mt-3">
              <AudioPlayerForRouting src={recordingUrl} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TwilanswerAppUI;
